import React, { useEffect, useState } from 'react';
import {
  DataTable,
  DataTablePageEvent,
  DataTableRowClickEvent,
} from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { SecurityGroupEntity } from '../../../../../api/types/settings/permissions/security-group-entity';
import { formatDate } from '../../../../../utils/date-format';
import { getAllSecurityGroupsOperation } from '../../operations/get-all-security-groups-operation';
import { deleteSecurityGroupOperation } from '../../operations/delete-security-group-operation';
import { CreateSecurityGroupDialog } from '../../components/create-security-group-dialog';
import { EditSecurityGroupDialog } from '../../components/edit-security-group-dialog';
import { PermissionGroupPageDialog } from './dialogs/permission-group-page-dialogs';
import { WarningDialog } from '../../../../../component/ui/warning-dialog/warning-dialog';

export const PermissionsGroupsTab = () => {
  const [data, setData] = useState<SecurityGroupEntity[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(false);
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [groupToEdit, setGroupToEdit] = useState<SecurityGroupEntity | null>(
    null,
  );
  const [groupToDelete, setGroupToDelete] =
    useState<SecurityGroupEntity | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<boolean>(false);

  const fetchSecurityGroups = async (nextOffset: number, nextLimit: number) => {
    await getAllSecurityGroupsOperation(
      nextOffset,
      nextLimit,
      setData,
      setTotal,
      setLoading,
    );
  };

  useEffect(() => {
    void fetchSecurityGroups(offset, limit);
  }, []);

  const onPageChange = async (event: DataTablePageEvent) => {
    const newOffset = event.first;
    const newLimit = event.rows;
    setOffset(newOffset);
    setLimit(newLimit);
    await fetchSecurityGroups(newOffset, newLimit);
  };

  const onRowClick = (event: DataTableRowClickEvent) => {
    const row = event.data as SecurityGroupEntity;
    if (row?.id) {
      setSelectedGroupId(row.id);
    }
  };

  const handleConfirmDelete = async () => {
    if (!groupToDelete) {
      return;
    }

    await deleteSecurityGroupOperation(
      groupToDelete.id,
      setDeletingGroup,
      () => {
        setGroupToDelete(null);
        void fetchSecurityGroups(offset, limit);
      },
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '12px',
        direction: 'rtl',
      }}
    >
      <CreateSecurityGroupDialog
        visible={showCreateDialog}
        onHide={() => setShowCreateDialog(false)}
        onCreated={() => fetchSecurityGroups(offset, limit)}
      />

      <PermissionGroupPageDialog
        visible={selectedGroupId !== null}
        groupId={selectedGroupId}
        onHide={() => setSelectedGroupId(null)}
      />

      <EditSecurityGroupDialog
        visible={groupToEdit !== null}
        group={groupToEdit}
        onHide={() => setGroupToEdit(null)}
        onUpdated={() => fetchSecurityGroups(offset, limit)}
      />

      <WarningDialog
        isVisible={groupToDelete !== null}
        header="מחיקת קבוצת הרשאות"
        addIcon
        content="האם אתה בטוח שברצונך למחוק את קבוצת ההרשאות?"
        quoteName={groupToDelete?.name}
        dangerousContent="פעולה זו אינה ניתנת לשחזור."
        requiredTypeingToApproved="delete"
        confirmLabel="מחיקה"
        confirmLoading={deletingGroup}
        onHide={() => setGroupToDelete(null)}
        onConfirm={handleConfirmDelete}
      />

      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
          padding: '12px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            marginBottom: '12px',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{ fontSize: '14px', color: '#4b5563', textAlign: 'right' }}
          >
            סה״כ קבוצות הרשאות: {total.toLocaleString()}
          </div>

          <Button
            label="הוספת קבוצת הרשאות"
            icon="pi pi-plus"
            onClick={() => setShowCreateDialog(true)}
            style={{ height: '36px' }}
          />
        </div>

        <DataTable
          value={data}
          lazy
          paginator
          rows={limit}
          first={offset}
          totalRecords={total}
          onPage={onPageChange}
          onRowClick={onRowClick}
          loading={loading}
          dataKey="id"
          rowHover
          tableStyle={{ direction: 'rtl' }}
          style={{ cursor: 'pointer' }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
          }
        >
          <Column field="id" header="מזהה" style={{ width: '80px' }} />
          <Column
            field="name"
            header="שם"
            style={{ textAlign: 'right', minWidth: '200px' }}
          />
          <Column
            field="description"
            header="תיאור"
            body={(row: SecurityGroupEntity) => row.description?.trim() || '—'}
            style={{ textAlign: 'right', minWidth: '240px' }}
          />
          <Column
            field="createdAt"
            header="תאריך יצירה"
            body={(row: SecurityGroupEntity) => formatDate(row.createdAt)}
            style={{ textAlign: 'right', width: '140px' }}
          />
          <Column
            field="createdByName"
            header="נוצר על ידי"
            body={(row: SecurityGroupEntity) =>
              row.createdByName?.trim() || '—'
            }
            style={{ textAlign: 'right', width: '160px' }}
          />
          <Column
            header=""
            body={(row: SecurityGroupEntity) => (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '4px',
                  textAlign: 'right',
                }}
              >
                <Button
                  icon="pi pi-pencil"
                  className="p-button-rounded p-button-text p-button-warning"
                  tooltip="עריכה"
                  tooltipOptions={{ position: 'top' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setGroupToEdit(row);
                  }}
                />
                <Button
                  icon="pi pi-trash"
                  className="p-button-rounded p-button-text p-button-danger"
                  tooltip="מחיקה"
                  tooltipOptions={{ position: 'top' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setGroupToDelete(row);
                  }}
                />
              </div>
            )}
            style={{ width: '120px', textAlign: 'center' }}
          />
        </DataTable>
      </div>
    </div>
  );
};
